import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "./Button";

interface NewsletterFormProps {
  className?: string;
  variant?: "simple" | "full";
  onSuccess?: () => void;
}

const NewsletterForm: React.FC<NewsletterFormProps> = ({ 
  className, 
  variant = "simple",
  onSuccess 
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    newsletterConsent: false,
    privacyConsent: false,
  });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">(
    "idle"
  );
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) return;
    
    if (variant === "full" && (!formData.privacyConsent)) {
        setMessage(t("newsletter.privacyRequired", "Please accept the privacy policy"));
        setStatus("error");
        return;
    }

    setStatus("loading");
    setMessage("");

    const portalId = "146949499";
    const formId = "606c3ac2-86ce-4639-ab22-72bd88581175";
    const url = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;

    try {
      const fields = [
        { name: "email", value: formData.email },
      ];

      if (variant === "full") {
        if (formData.firstName) fields.push({ name: "firstname", value: formData.firstName });
        if (formData.lastName) fields.push({ name: "lastname", value: formData.lastName });
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fields,
          context: {
            pageUri: window.location.href,
            pageName: document.title,
          },
          legalConsentOptions: variant === "full" ? {
            consent: {
              consentToProcess: true,
              text: "I agree to allow VettConsult to store and process my personal data.",
              communications: [
                {
                  value: formData.newsletterConsent,
                  subscriptionTypeId: 999, // You might need the actual subscription type ID from HubSpot
                  text: "I agree to receive marketing communications from VettConsult."
                }
              ]
            }
          } : undefined
        }),
      });

      if (response.ok) {
        setStatus("success");
        setMessage(t("newsletter.success", "Thanks for subscribing!"));
        setFormData({
            email: "",
            firstName: "",
            lastName: "",
            newsletterConsent: false,
            privacyConsent: false,
        });
        if (onSuccess) {
            setTimeout(onSuccess, 2000);
        }
      } else {
        const data = await response.json();
        throw new Error(data.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Newsletter submission error:", error);
      setStatus("error");
      setMessage(
        t("newsletter.error", "Something went wrong. Please try again.")
      );
    }
  };

  const inputClasses = "w-full px-4 py-3 rounded-lg bg-white/50 backdrop-blur-sm border border-gray-200 focus:border-[#005a50] focus:ring-2 focus:ring-[#005a50]/20 outline-none transition-all duration-200 text-gray-800 placeholder-gray-500";

  if (variant === "full") {
    return (
        <div className={`w-full ${className || ""}`}>
            {status === "success" ? (
                <div className="p-6 rounded-xl bg-green-50 border border-green-200 text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <h3 className="text-xl font-bold text-green-800 mb-2">{t("newsletter.successTitle", "Welcome!")}</h3>
                    <p className="text-green-700">{message}</p>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("newsletter.firstName", "Name")}</label>
                            <input
                                type="text"
                                value={formData.firstName}
                                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                                className={inputClasses}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("newsletter.lastName", "Surname")}</label>
                            <input
                                type="text"
                                value={formData.lastName}
                                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                                className={inputClasses}
                            />
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("newsletter.email", "Email")} *</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            className={inputClasses}
                            required
                        />
                    </div>

                    <div className="space-y-3 pt-2">
                        <label className="flex items-start gap-3 cursor-pointer group">
                            <div className="relative flex items-center justify-center mt-1">
                                <input 
                                    type="checkbox" 
                                    checked={formData.newsletterConsent}
                                    onChange={(e) => setFormData({...formData, newsletterConsent: e.target.checked})}
                                    className="peer appearance-none w-5 h-5 border-2 border-gray-300 rounded bg-white checked:bg-[#005a50] checked:border-[#005a50] transition-colors duration-200 cursor-pointer"
                                />
                                <svg 
                                    className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200 pointer-events-none" 
                                    fill="none" 
                                    viewBox="0 0 24 24" 
                                    stroke="currentColor" 
                                    strokeWidth="3"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">{t("newsletter.consentNewsletter", "Yes, I want to receive VettConsult's newsletter by email.")}</span>
                        </label>
                        
                        <label className="flex items-start gap-3 cursor-pointer group">
                            <div className="relative flex items-center justify-center mt-1">
                                <input 
                                    type="checkbox" 
                                    checked={formData.privacyConsent}
                                    onChange={(e) => setFormData({...formData, privacyConsent: e.target.checked})}
                                    className="peer appearance-none w-5 h-5 border-2 border-gray-300 rounded bg-white checked:bg-[#005a50] checked:border-[#005a50] transition-colors duration-200 cursor-pointer"
                                    required
                                />
                                <svg 
                                    className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200 pointer-events-none" 
                                    fill="none" 
                                    viewBox="0 0 24 24" 
                                    stroke="currentColor" 
                                    strokeWidth="3"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                                {t("newsletter.consentPrivacy", "I authorize the processing of my data in accordance with VettConsult's privacy policy.")}
                            </span>
                        </label>
                    </div>

                    <Button
                        type="submit"
                        variant="primary"
                        disabled={status === "loading"}
                        className="w-full mt-4"
                        style={{
                            backgroundColor: "#005a50",
                            color: "#ffffff",
                        }}
                    >
                        {status === "loading"
                        ? t("newsletter.submitting", "Subscribing...")
                        : t("newsletter.button", "Subscribe")}
                    </Button>
                    
                    {status === "error" && (
                        <p className="text-sm text-red-500 text-center">{message}</p>
                    )}
                </form>
            )}
        </div>
    );
  }

  return (
    <div className={`w-full max-w-md mx-auto ${className || ""}`}>
      {status === "success" ? (
        <div className="p-4 rounded-lg bg-green-50/10 border border-green-500/20 text-center">
          <p className="text-green-600 dark:text-green-400 font-medium">
            {message}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-grow">
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder={t("newsletter.placeholder", "Enter your email")}
              className={inputClasses}
              required
              disabled={status === "loading"}
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            disabled={status === "loading"}
            className="w-full sm:w-auto whitespace-nowrap"
            style={{
                backgroundColor: "#005a50",
                color: "#ffffff",
            }}
          >
            {status === "loading"
              ? t("newsletter.submitting", "Subscribing...")
              : t("newsletter.button", "Subscribe")}
          </Button>
        </form>
      )}
      {status === "error" && (
        <p className="mt-2 text-sm text-red-500 text-center">{message}</p>
      )}
    </div>
  );
};

export default NewsletterForm;
